import { useAuth } from "@/hooks/useAuth";
import {
  trackReportEditSuccess,
  trackReportFormCancel,
  trackReportFormOpen,
  trackReportSubmitError,
  trackReportSubmitSuccess,
} from "@/lib/analytics";
import { Camera } from "@/lib/regions";
import { validateComment } from "@/lib/contentFilter";
import {
  canReportForSpot,
  formatCooldown,
  getRemainingCooldown,
  recordReport,
} from "@/lib/rateLimit";
import { submitReport, updateReport } from "@/lib/reports";
import {
  SurfReport,
  WAVE_SIZES,
  CONDITIONS,
  CROWD_LEVELS,
  WaveSize,
  Condition,
  CrowdLevel,
} from "@/types/surfReport";
import PlaceIcon from "@mui/icons-material/Place";
import TimerIcon from "@mui/icons-material/Timer";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import MediaUpload from "./MediaUpload";
import OptionSelector from "./OptionSelector";

interface ReportFormDialogProps {
  open: boolean;
  onClose: () => void;
  regionId: string;
  regionLabel: string;
  camera: Camera | null;
  editingReport?: SurfReport | null;
}

const waveSizeOptions = Object.entries(WAVE_SIZES).map(([value, cfg]) => ({
  value,
  label: cfg.label,
}));

const conditionOptions = Object.entries(CONDITIONS).map(([value, cfg]) => ({
  value,
  label: cfg.label,
  color: cfg.color,
}));

const crowdOptions = Object.entries(CROWD_LEVELS).map(([value, cfg]) => ({
  value,
  label: cfg.label,
  color: cfg.color,
}));

export default function ReportFormDialog({
  open,
  onClose,
  regionId,
  regionLabel,
  camera,
  editingReport,
}: ReportFormDialogProps) {
  const { user } = useAuth();
  const [waveSize, setWaveSize] = useState<WaveSize | null>(null);
  const [conditions, setConditions] = useState<Condition | null>(null);
  const [crowd, setCrowd] = useState<CrowdLevel | null>(null);
  const [comment, setComment] = useState("");
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Distinguishes "closed because the form submitted" from "closed because
  // the user cancelled". Set true in the submit path; read in handleClose.
  const submittedRef = useRef(false);

  const isEditing = !!editingReport;

  // Fire `report_form_open` once per dialog-open transition so we can
  // build a funnel against `report_submit_success` and `report_form_cancel`.
  useEffect(() => {
    if (!open) return;
    submittedRef.current = false;
    trackReportFormOpen(editingReport ? "edit" : "create");
  }, [open, editingReport]);
  const spotKey = camera?.id ?? regionId;
  const rateLimited = !isEditing && !canReportForSpot(spotKey);
  const cooldownRemaining = getRemainingCooldown(spotKey);

  const canSubmit = waveSize && conditions && crowd && !submitting && !rateLimited;

  // Pre-fill form when editing
  useEffect(() => {
    if (editingReport && open) {
      setWaveSize(editingReport.waveSize);
      setConditions(editingReport.conditions);
      setCrowd(editingReport.crowd);
      setComment(editingReport.comment || "");
    }
  }, [editingReport, open]);

  const handleSubmit = async () => {
    if (!canSubmit || !user) return;

    // Validate comment
    if (comment) {
      const commentError = validateComment(comment);
      if (commentError) {
        setError(commentError);
        return;
      }
    }

    // Double-check rate limit for new reports
    if (!isEditing && !canReportForSpot(spotKey)) {
      setError(
        `You can report again in ${formatCooldown(getRemainingCooldown(spotKey))}.`,
      );
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (isEditing) {
        await updateReport({
          reportId: editingReport.id,
          waveSize,
          conditions,
          crowd,
          comment: comment || undefined,
        });
        trackReportEditSuccess();
        submittedRef.current = true;
      } else {
        await submitReport({
          regionId,
          cameraId: camera?.id ?? null,
          cameraName: camera?.name ?? null,
          user,
          waveSize,
          conditions,
          crowd,
          comment: comment || undefined,
          mediaFile: mediaFile || undefined,
        });
        recordReport(spotKey);
        trackReportSubmitSuccess(regionId, waveSize);
        submittedRef.current = true;
      }
      handleClose();
    } catch (err) {
      setError("Failed to submit — check your connection and try again.");
      setSubmitting(false);
      trackReportSubmitError((err as Error)?.message ?? "unknown");
    }
  };

  const handleClose = () => {
    if (!submittedRef.current) trackReportFormCancel();
    setWaveSize(null);
    setConditions(null);
    setCrowd(null);
    setComment("");
    setMediaFile(null);
    setSubmitting(false);
    setError(null);
    onClose();
  };

  const dialogTitle = isEditing
    ? "Edit report"
    : camera
      ? `Report conditions — ${camera.name}`
      : `Report conditions — ${regionLabel}`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem", pb: 1 }}>{dialogTitle}</DialogTitle>
      <DialogContent>
        {rateLimited ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <TimerIcon sx={{ fontSize: 36, color: "rgba(255,255,255,0.2)", mb: 1 }} />
            <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 0.5 }}>
              You recently reported for this spot.
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
              You can submit again in <strong>{formatCooldown(cooldownRemaining)}</strong>
              .
            </Typography>
          </Box>
        ) : (
          <>
            {/* Location indicator */}
            {camera && !isEditing && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 1.5,
                  px: 0.5,
                }}
              >
                <PlaceIcon sx={{ fontSize: 14, color: "primary.main", opacity: 0.7 }} />
                <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                  {camera.name}
                </Typography>
              </Box>
            )}

            <OptionSelector
              label="Wave Size"
              options={waveSizeOptions}
              value={waveSize}
              onChange={(v) => setWaveSize(v as WaveSize)}
            />
            <OptionSelector
              label="Conditions"
              options={conditionOptions}
              value={conditions}
              onChange={(v) => setConditions(v as Condition)}
            />
            <OptionSelector
              label="Crowd"
              options={crowdOptions}
              value={crowd}
              onChange={(v) => setCrowd(v as CrowdLevel)}
            />
            <TextField
              fullWidth
              size="small"
              placeholder="Quick note (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 280))}
              inputProps={{ maxLength: 280 }}
              helperText={comment.length > 0 ? `${comment.length}/280` : ""}
              sx={{
                mt: 1,
                "& .MuiInputBase-input": { fontSize: "0.85rem" },
                "& .MuiFormHelperText-root": {
                  textAlign: "right",
                  fontSize: "0.65rem",
                },
              }}
            />
            {!isEditing && <MediaUpload file={mediaFile} onFileSelect={setMediaFile} />}
          </>
        )}
        {error && (
          <Typography sx={{ mt: 1.5, fontSize: "0.8rem", color: "error.main" }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} size="small" sx={{ color: "text.secondary" }}>
          {rateLimited ? "Close" : "Cancel"}
        </Button>
        {!rateLimited && (
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{ textTransform: "none", minWidth: 100 }}
          >
            {submitting ? <CircularProgress size={18} /> : isEditing ? "Save" : "Submit"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
